/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-10 16:03:18
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Layout } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { URL } from 'src/constants/links';
import { NUMBER_SYNONYMS_PER_PAGE } from 'src/constants/synonym';
import { allowUsingNewSearchSettings } from 'src/helpers/search-settings-helper';

import ImportModal from './import';
import ExportModal from './export';
import componentWithSyncWrapper from 'src/components/containers/common/component-with-sync-wrapper';
import TypeFormEmbeded from 'src/components/views/common/typeform-embedded';
import YoutubeModal from 'src/components/views/common/youtube-modal';
import AISynonymSetting from 'src/components/views/synonym/ai-synonym-setting';
import TitleHelp from 'src/components/views/common/title-help';
import history from 'src/routes/history';

import * as synonymActions from 'src/stores/actions/synonym-actions';
import AIRunningBanner from 'src/components/views/synonym/ai-running-banner';
import { routes } from 'src/routes';

type Props = {
  getSynonyms: (q: string, skip: number, limit: number) => void,
  synonyms: Array<Object>,
  loading: boolean,
  success: boolean,
  updateSuccess: boolean,
  successFlag: number,
  saving: boolean,
  importing: boolean,
  scheduleSyncIn: number,
  waitSyncAt: number,
  selectedPlan: string
};

type ComposedProps = Props & WithI18nProps;

type State = {
  activeImportModal: boolean,
  activeExportModal: boolean,
  firstPageLoading: boolean,
  fromSynonymPageState: number,
  scheduleSyncIn: number,
  showScheduleSync: boolean,
  youtubeModal: boolean
};

class AISynonym extends Component<ComposedProps, State> {
  timerTyping = undefined;

  state = {
    // Import modal
    activeImportModal: false,
    // Export modal
    activeExportModal: false,
    firstPageLoading: true,
    fromSynonymPageState: 0,
    scheduleSyncIn: 0,
    showScheduleSync: false,
    youtubeModal: false
  };

  componentDidMount() {
    // Show typeform when go back from new/edit page
    const fromPage = _.get(this.props.location, 'state.fromPage', '');
    const tab = _.get(this.props.location, 'state.tab', 0);
    if (!_.isEmpty(fromPage)) {
      const { fromSynonymPageState } = this.state;
      this.setState({ fromSynonymPageState: fromSynonymPageState + 1 });
    }

    if (tab) this.setState({ selectedTab: tab });
  }

  componentWillReceiveProps(nextProps) {
    const { saving, importing, scheduleSyncIn, waitSyncAt } = nextProps;
    const {
      saving: savingPrev,
      importing: importingPrev,
      getSyncProcessStatus,
      updateShowBanner
    } = this.props;

    // Check to show banner Processing update when change status/ import
    if ((savingPrev && !saving) || (importingPrev && !importing)) {
      if (waitSyncAt) {
        // Time from now to sync point
        let syncDuration = waitSyncAt - Math.floor(Date.now() / 1000);
        syncDuration = syncDuration > 0 ? syncDuration : 0;
        // Time to finish sync point
        syncDuration = Math.floor(syncDuration / 60) + scheduleSyncIn;

        updateShowBanner(syncDuration, true);
      }

      // Get process sync status
      getSyncProcessStatus();
    }
  }

  render() {
    const { i18n, updateSuccess, selectedPlan, enableAISearch, isNewSearch } = this.props;
    const { fromSynonymPageState, youtubeModal } = this.state;

    return (
      <div className="BC-Page BC-Page__Synonym">
        <Page title={i18n.translate('AdminHTML.Synonym.Synonym.aiTab')}>
          <TitleHelp
            i18n={i18n}
            hrefUserManual={URL.Link_V2_Search_Synonym_ManualGuide}
            onAnalytics={() => history.push(routes.analytics.overviewSearch())}
          />

          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {enableAISearch && isNewSearch && <AIRunningBanner />}
              </div>
            </Layout.Section>

            {/* List of synonym */}
            <Layout.Section>
              <AISynonymSetting isNewSearch={isNewSearch} enableAISearch={enableAISearch} />
            </Layout.Section>
          </Layout>
        </Page>

        <ImportModal
          active={this.state.activeImportModal}
          handleChange={this.handleChangeImportModal}
          getSynonymsFirstPage={this.getSynonymsFirstPage}
          history={history}
        />

        <ExportModal
          active={this.state.activeExportModal}
          handleChange={this.handleChangeExportModal}
        />

        {!_.isEmpty(selectedPlan) && (
          <TypeFormEmbeded
            type="synonym"
            conditionShow={updateSuccess}
            conditionShowStateChange={fromSynonymPageState}
          />
        )}
        <YoutubeModal
          active={youtubeModal}
          embeddedURL={URL['SynonymsTut']}
          title={i18n.translate('AdminHTML.Synonym.Synonym.youtubeModal_Title')}
          onClose={this.handleCloseYoutubeModal}
        />
      </div>
    );
  }

  //
  getSynonymsFirstPage = () => {
    this.props.getSynonyms('', 0, NUMBER_SYNONYMS_PER_PAGE, '', '');
  };

  // Hide/show import modal
  handleChangeImportModal = () => {
    this.setState(({ activeImportModal }) => ({
      activeImportModal: !activeImportModal
    }));
  };

  // Hide/show export modal
  handleChangeExportModal = () => {
    this.setState(({ activeExportModal }) => ({
      activeExportModal: !activeExportModal
    }));
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  handleOpenYoutubeModal = () => this.setState({ youtubeModal: true });
  handleCloseYoutubeModal = () => this.setState({ youtubeModal: false });
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getSynonyms: (q, skip, limit, sort, status) =>
    dispatch(synonymActions.getSynonyms({ q, skip, limit, sort, status }, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const searchVersion = _.get(state.oauth, 'tenantApp.parseParams.searchVersion', '1.0');
  const isNewSearch = allowUsingNewSearchSettings(searchVersion);

  return {
    isNewSearch,
    enableAISearch: _.get(
      state.oauth.tenantApp,
      'settings.instantSearch.settings.AI-synonyms',
      false
    ),
    synonyms: _.get(state.synonym, 'synonyms', [{}, {}, {}]),
    loading: _.get(state.synonym, 'loading', false),
    success: _.get(state.synonym, 'success', false),
    updateSuccess: _.get(state.synonym, 'updateSuccess', false),
    successFlag: _.get(state.synonym, 'successFlag', 0),
    saving: _.get(state.synonym, 'saving', false),
    importing: _.get(state.synonym, 'importing', false),
    scheduleSyncIn: _.get(state.synonym, 'scheduleSyncIn', 0),
    waitSyncAt: _.get(state.synonym, 'waitSyncAt', 0),
    selectedPlan: _.get(state.oauth.tenantApp, 'selectedPlan'),
    settings: _.get(state.oauth.tenantApp, 'settings', {})
  };
};

export default componentWithSyncWrapper(
  withI18n()(connect(mapStateToProps, mapDispatchToProps)(AISynonym)),
  'SyncSynonym'
);
